/**
 * Contador de peticiones en vuelo, para el velo de carga global.
 *
 * No usa contexto de React a propósito: lo alimentan los interceptores de
 * axios, que viven fuera del árbol de componentes. Un simple contador con
 * suscriptores es todo lo que hace falta.
 */

type Escucha = (visible: boolean) => void;

let enVuelo = 0;
let visible = false;
let temporizadorMostrar: ReturnType<typeof setTimeout> | null = null;
let mostradoEn = 0;
const escuchas = new Set<Escucha>();

/**
 * Antes de mostrar se esperan 350 ms. En una red normal casi ninguna petición
 * llega a ese umbral, así que el velo NO aparece y la aplicación se siente
 * instantánea; solo se ve cuando de verdad hay que esperar. Un velo que
 * parpadea 80 ms es ruido, no información.
 */
const RETARDO = 350;

/**
 * Y una vez visible se queda al menos 400 ms. Sin esto, una petición de 360 ms
 * haría un destello imposible de leer.
 */
const MINIMO = 400;

const avisar = () => escuchas.forEach((f) => f(visible));

const mostrar = () => {
  visible = true;
  mostradoEn = Date.now();
  avisar();
};

const ocultar = () => {
  const llevaPuesto = Date.now() - mostradoEn;
  if (llevaPuesto < MINIMO) {
    setTimeout(() => { if (enVuelo === 0) { visible = false; avisar(); } }, MINIMO - llevaPuesto);
    return;
  }
  visible = false;
  avisar();
};

/** Empieza una espera. `inmediato` para guardar, borrar y demás escrituras. */
export const empezar = (inmediato = false) => {
  enVuelo++;
  if (visible) return;
  if (inmediato) { mostrar(); return; }
  if (!temporizadorMostrar) {
    temporizadorMostrar = setTimeout(() => {
      temporizadorMostrar = null;
      if (enVuelo > 0) mostrar();
    }, RETARDO);
  }
};

/** Termina una espera. */
export const terminar = () => {
  enVuelo = Math.max(0, enVuelo - 1);
  if (enVuelo > 0) return;
  if (temporizadorMostrar) { clearTimeout(temporizadorMostrar); temporizadorMostrar = null; }
  if (visible) ocultar();
};

export const suscribirse = (f: Escucha) => {
  escuchas.add(f);
  f(visible);
  return () => { escuchas.delete(f); };
};
